import { useState } from "react";
import type { FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";

const FEATURES = [
  "Drag-and-drop Handlebars template designer with live preview",
  "Connect any external data source and map its fields to your report",
  "Schedule reports and auto-email the results, single file or zipped",
];

function CheckIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="8" cy="8" r="8" fill="currentColor" fillOpacity="0.15" />
      <path d="M4.5 8.2 6.8 10.5 11.5 5.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function slugify(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export default function Signup() {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [companyName, setCompanyName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugEdited, setSlugEdited] = useState(false);
  const [ownerFullName, setOwnerFullName] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  function handleCompanyNameChange(value: string) {
    setCompanyName(value);
    if (!slugEdited) setSlug(slugify(value));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (password !== confirmPassword) {
      setError("Passwords don't match");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    setSubmitting(true);
    try {
      await signup({
        companyName: companyName.trim(),
        slug: slug.trim(),
        ownerFullName: ownerFullName.trim() || undefined,
        ownerEmail: ownerEmail.trim(),
        password,
      });
      navigate("/getting-started", { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Signup failed");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="login-shell">
      <div className="login-marketing">
        <div className="login-marketing-inner">
          <div className="login-wordmark">ReportForge</div>
          <div className="login-tagline">
            Start a free trial and have a full go at Report Studio - no credit card needed.
          </div>
          <ul className="login-features">
            {FEATURES.map((f) => (
              <li key={f}>
                <CheckIcon />
                <span>{f}</span>
              </li>
            ))}
          </ul>
          <div className="code-block">
            {"<h2>{{report.displayName}}</h2>\n<p>Hello {{params.customerName}}</p>"}
          </div>
        </div>
      </div>

      <div className="login-form-side">
        <form className="login-card" onSubmit={handleSubmit}>
          <h2>Start your free trial</h2>
          <p className="help-text">Your own workspace, ready in seconds. No card required.</p>

          <div className="form-row">
            <label htmlFor="companyName">Company / team name</label>
            <input
              id="companyName"
              required
              value={companyName}
              onChange={(e) => handleCompanyNameChange(e.target.value)}
              placeholder="Acme Sacco"
            />
          </div>

          <div className="form-row">
            <label htmlFor="slug">Workspace URL</label>
            <input
              id="slug"
              required
              value={slug}
              onChange={(e) => {
                setSlugEdited(true);
                setSlug(slugify(e.target.value));
              }}
              placeholder="acme-sacco"
            />
          </div>

          <div className="form-row">
            <label htmlFor="ownerFullName">Your name</label>
            <input
              id="ownerFullName"
              value={ownerFullName}
              onChange={(e) => setOwnerFullName(e.target.value)}
            />
          </div>

          <div className="form-row">
            <label htmlFor="ownerEmail">Email</label>
            <input
              id="ownerEmail"
              type="email"
              required
              value={ownerEmail}
              onChange={(e) => setOwnerEmail(e.target.value)}
            />
          </div>

          <div className="form-row">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>

          <div className="form-row">
            <label htmlFor="confirmPassword">Confirm password</label>
            <input
              id="confirmPassword"
              type="password"
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </div>

          {error && <div className="error-text">{error}</div>}

          <button type="submit" className="btn-primary" disabled={submitting} style={{ marginTop: 12, width: "100%" }}>
            {submitting ? "Creating your workspace..." : "Start free trial"}
          </button>

          <p className="help-text" style={{ marginTop: 12, textAlign: "center" }}>
            Already have an account? <Link to="/login">Sign in</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
